/**
 * AiAgentsSettingsPage — panel ustawień 3 agentów AI.
 *
 * Każdy agent ma osobną kartę:
 *   - Aktywny (switch)
 *   - Provider (select: openai/anthropic/gemini/disabled)
 *   - Model (input)
 *   - System prompt / User prompt template (textarea)
 *   - Temperature, Max tokens
 *   - Zapisz
 *
 * Banner status kluczy ENV (OPENAI/ANTHROPIC/GEMINI) — bez wartości klucza.
 * Warning gdy wybrany provider != skonfigurowany.
 *
 * Klucze API: TYLKO z ENV, NIE są wysyłane ani odbierane przez frontend.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Bot, Save, AlertTriangle, CheckCircle2, ChevronLeft, Loader2, Zap, FlaskConical, Brain } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { logger } from "../../utils/logger";
import AgentPlaygroundDialog from "./AgentPlaygroundDialog";
import VendorMemoryDialog from "./VendorMemoryDialog";
import ApiKeysCard from "./ApiKeysCard";
import OpenRouterModelSelector from "./OpenRouterModelSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDER_OPTIONS = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic Claude" },
  { value: "gemini", label: "Google Gemini" },
  { value: "minimax", label: "MiniMax" },
  { value: "openrouter", label: "OpenRouter" },
  { value: "disabled", label: "Wyłączony (Disabled)" },
];

const AGENT_ORDER = ["invoice_agent", "bank_agent", "payment_agent"];

// P0 (2026-06-24): rekomendowana kolejność rodzin Anthropic.
// Cel: pokazać tanie/szybkie modele wyżej (Haiku jako default zamiast losowego
// modelu z listy). Auto-pick frontu wybiera pierwszy element po sortowaniu.
const ANTHROPIC_FAMILY_ORDER = ["haiku", "sonnet", "opus"];

function _anthropicFamilyIdx(modelId) {
  const lower = String(modelId || "").toLowerCase();
  for (let i = 0; i < ANTHROPIC_FAMILY_ORDER.length; i++) {
    if (lower.includes(ANTHROPIC_FAMILY_ORDER[i])) return i;
  }
  return ANTHROPIC_FAMILY_ORDER.length; // nieznane na końcu
}

function sortAnthropicModels(list) {
  // 1) rodzina (haiku → sonnet → opus → inne)
  // 2) w obrębie rodziny: dated snapshot (zawiera "-20" jako prefix daty) PRZED aliasem,
  //    bo dated jest stabilniejszy w produkcji
  // 3) leksykograficznie malejąco — nowsze daty wyżej
  return [...list].sort((a, b) => {
    const fa = _anthropicFamilyIdx(a);
    const fb = _anthropicFamilyIdx(b);
    if (fa !== fb) return fa - fb;
    const da = /-20\d{6}$/.test(a) ? 0 : 1;
    const db = /-20\d{6}$/.test(b) ? 0 : 1;
    if (da !== db) return da - db;
    return b.localeCompare(a);
  });
}

const ProviderStatusBadge = ({ ok, label }) => (
  <div
    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs ${
      ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
         : "bg-gray-50 text-gray-600 border border-gray-200"
    }`}
    data-testid={`provider-status-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    <span className="font-medium">{label}:</span>
    <span>{ok ? "skonfigurowany" : "brak klucza"}</span>
  </div>
);

const AgentCard = ({ agent, providers, onSave, saving, onOpenPlayground, onOpenMemory }) => {
  const [local, setLocal] = useState(agent);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Dynamic models per provider
  const [models, setModels] = useState([]);
  const [modelsMetadata, setModelsMetadata] = useState(null); // dla openrouter (list[dict])
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState("");
  const [previousModel, setPreviousModel] = useState("");

  useEffect(() => { setLocal(agent); setTestResult(null); }, [agent]);

  const providerSelected = local.provider || "disabled";

  // Fetch modeli przy zmianie providera (z wyjątkiem 'disabled')
  useEffect(() => {
    setModels([]);
    setModelsMetadata(null);
    setModelsError("");
    if (providerSelected === "disabled") return;
    setPreviousModel(local.model || ""); // zapamiętaj poprzedni przed fetch'em
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try {
        const res = await axios.get(
          `${API}/ai/providers/${providerSelected}/models`,
          { withCredentials: true },
        );
        if (cancelled) return;
        if (res.data?.ok) {
          const rawList = res.data.models || [];
          // OpenRouter: pełne metadane w `models_metadata` (list[dict]).
          // Nowy selektor (OpenRouterModelSelector) sam zarządza listą i auto-pickiem.
          if (providerSelected === "openrouter" && Array.isArray(res.data.models_metadata)) {
            setModelsMetadata(res.data.models_metadata);
            setModels(rawList); // i tak trzymamy id-list dla walidacji
            return;
          }
          // P0 (2026-06-24): dla Anthropic sortujemy: Haiku (najtańsze) → Sonnet
          // → Opus → pozostałe. W obrębie rodziny: dated snapshot (zawiera "-20")
          // przed aliasem. To pozwala userowi szybko wybrać tani Haiku.
          const list = providerSelected === "anthropic"
            ? sortAnthropicModels(rawList)
            : rawList;
          setModels(list);
          // Auto-wybór pierwszego modelu gdy obecny pusty LUB nie pasuje do nowej listy
          if (list.length > 0) {
            setLocal((s) => {
              if (!s.model || !list.includes(s.model)) {
                return { ...s, model: list[0] };
              }
              return s;
            });
          }
        } else {
          setModelsError(res.data?.error || "Nie udało się pobrać listy modeli.");
        }
      } catch (e) {
        if (!cancelled) {
          logger.error("models fetch error", e);
          setModelsError("Nie udało się pobrać listy modeli.");
        }
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerSelected, agent.agent_id]);

  const providerCfg = providers?.[providerSelected];
  const showWarning =
    providerSelected !== "disabled" && providerCfg && !providerCfg.configured;

  // Banner mismatch: aktualny provider bez klucza, ale inny ma → sugeruje fix.
  const availableConfiguredProvider = React.useMemo(() => {
    if (!showWarning || !providers) return null;
    for (const p of ["openai", "anthropic", "gemini", "minimax"]) {
      if (p !== providerSelected && providers[p]?.configured) return p;
    }
    return null;
  }, [showWarning, providers, providerSelected]);
  const providerLabel = (id) =>
    (PROVIDER_OPTIONS.find((o) => o.value === id) || {}).label || id;
  const modelNotInList =
    providerSelected !== "disabled" &&
    !modelsLoading &&
    !modelsError &&
    models.length > 0 &&
    local.model &&
    !models.includes(local.model);

  const set = (field, value) => setLocal((s) => ({ ...s, [field]: value }));

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(
        `${API}/ai/agents/settings/${agent.agent_id}/test-connection`,
        {},
        { withCredentials: true },
      );
      setTestResult(res.data);
    } catch (e) {
      logger.error("test-connection error", e);
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Błąd testu połączenia";
      setTestResult({ ok: false, error: msg, provider: agent.provider, model: agent.model });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card data-testid={`agent-card-${agent.agent_id}`}>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Bot className="w-5 h-5 text-blue-600" />
            {agent.agent_name || agent.agent_id}
          </CardTitle>
          <CardDescription className="text-xs">{agent.agent_id}</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor={`enabled-${agent.agent_id}`} className="text-xs text-gray-600">
            Aktywny
          </Label>
          <Switch
            id={`enabled-${agent.agent_id}`}
            checked={!!local.enabled}
            onCheckedChange={(v) => set("enabled", v)}
            data-testid={`agent-${agent.agent_id}-enabled-switch`}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Provider</Label>
            <Select
              value={providerSelected}
              onValueChange={(v) =>
                setLocal((s) => ({
                  ...s,
                  provider: v,
                  // Reset modelu przy zmianie providera — useEffect na 'models' wybierze
                  // pierwszy dostępny model dla nowego providera.
                  model: s.provider === v ? s.model : "",
                }))
              }
            >
              <SelectTrigger data-testid={`agent-${agent.agent_id}-provider`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROVIDER_OPTIONS.map((p) => (
                  <SelectItem
                    key={p.value}
                    value={p.value}
                    data-testid={`agent-${agent.agent_id}-provider-opt-${p.value}`}
                  >
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600 flex items-center gap-2">
              Model
              {modelsLoading && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
            </Label>
            {providerSelected === "disabled" ? (
              <Input
                value={local.model || ""}
                onChange={(e) => set("model", e.target.value)}
                placeholder="Agent wyłączony — model nieaktywny"
                disabled
                data-testid={`agent-${agent.agent_id}-model`}
              />
            ) : modelsError ? (
              <>
                <Input
                  value={local.model || ""}
                  onChange={(e) => set("model", e.target.value)}
                  placeholder="np. gpt-4o-mini"
                  data-testid={`agent-${agent.agent_id}-model`}
                />
                <div
                  className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1"
                  data-testid={`agent-${agent.agent_id}-models-error`}
                >
                  Nie udało się pobrać listy modeli.
                </div>
              </>
            ) : providerSelected === "openrouter" && Array.isArray(modelsMetadata) ? (
              <OpenRouterModelSelector
                models={modelsMetadata}
                value={local.model || ""}
                onChange={(v) => set("model", v)}
                previousModel={previousModel}
              />
            ) : (
              <>
                <Select
                  value={local.model || ""}
                  onValueChange={(v) => set("model", v)}
                  disabled={modelsLoading || models.length === 0}
                >
                  <SelectTrigger data-testid={`agent-${agent.agent_id}-model`}>
                    <SelectValue placeholder={
                      modelsLoading ? "Wczytywanie modeli…" :
                      models.length === 0 ? "Brak dostępnych modeli" : "Wybierz model"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Pokaż aktualnie zapisany model jeśli nie ma go na liście */}
                    {local.model && !models.includes(local.model) && (
                      <SelectItem value={local.model} disabled>
                        {local.model} (niedostępny)
                      </SelectItem>
                    )}
                    {models.map((m) => (
                      <SelectItem
                        key={m}
                        value={m}
                        data-testid={`agent-${agent.agent_id}-model-opt-${m}`}
                      >
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {modelNotInList && (
                  <div
                    className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1"
                    data-testid={`agent-${agent.agent_id}-model-not-available`}
                  >
                    Wybrany model nie jest dostępny dla tego providera.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {showWarning && (
          <div
            className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded px-3 py-2 flex items-start gap-2"
            data-testid={`agent-${agent.agent_id}-key-warning`}
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="space-y-1.5 flex-1">
              <div>
                <strong>Agent używa:</strong> {providerLabel(providerSelected)}
                {providerCfg?.env_var ? ` (${providerCfg.env_var})` : ""} — brak klucza API.
              </div>
              {availableConfiguredProvider && (
                <>
                  <div>
                    <strong>Skonfigurowany klucz:</strong> {providerLabel(availableConfiguredProvider)}.
                  </div>
                  <div>
                    Zmień providera na <strong>{providerLabel(availableConfiguredProvider)}</strong>{" "}
                    albo dodaj klucz <strong>{providerLabel(providerSelected)}</strong>.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setLocal((s) => ({
                        ...s,
                        provider: availableConfiguredProvider,
                        model: "",
                      }))
                    }
                    data-testid={`agent-${agent.agent_id}-fix-provider-btn`}
                    className="h-8 mt-1 border-amber-300 text-amber-900 hover:bg-amber-100"
                  >
                    Przełącz na {providerLabel(availableConfiguredProvider)}
                  </Button>
                </>
              )}
              {!availableConfiguredProvider && (
                <div>Agent nie wykona zapytań do LLM bez tego klucza.</div>
              )}
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">System prompt</Label>
          <Textarea
            value={local.system_prompt || ""}
            onChange={(e) => set("system_prompt", e.target.value)}
            rows={5}
            className="font-mono text-xs"
            data-testid={`agent-${agent.agent_id}-system-prompt`}
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">User prompt template</Label>
          <Textarea
            value={local.user_prompt_template || ""}
            onChange={(e) => set("user_prompt_template", e.target.value)}
            rows={4}
            className="font-mono text-xs"
            data-testid={`agent-${agent.agent_id}-user-prompt`}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-600">Temperature (0–2)</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={local.temperature ?? 0.2}
              onChange={(e) => set("temperature", parseFloat(e.target.value))}
              data-testid={`agent-${agent.agent_id}-temperature`}
            />
          </div>
          <div className="space-y-1.5">
            {/*
              2026-02: „Limit odpowiedzi LLM" — nowy przełącznik per-agent.
              Gdy WYŁĄCZONY (default) — pole liczbowe wyszarzone, backend
              NIE wysyła max_tokens do providera (parametr nie istnieje w
              request payload). Gdy WŁĄCZONY — pole aktywne, wysyłana jest
              DOKŁADNA wartość wpisana przez administratora (nawet 0).
            */}
            <div className="flex items-center justify-between gap-2">
              <Label className="text-xs text-gray-600">Limit odpowiedzi LLM</Label>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-500">Narzucaj własny limit odpowiedzi</span>
                <Switch
                  checked={!!local.max_tokens_enabled}
                  onCheckedChange={(v) => set("max_tokens_enabled", !!v)}
                  data-testid={`agent-${agent.agent_id}-max-tokens-enabled`}
                />
              </div>
            </div>
            <Input
              type="number"
              step="100"
              min="0"
              value={local.max_tokens ?? 10000}
              onChange={(e) => set("max_tokens", parseInt(e.target.value, 10))}
              data-testid={`agent-${agent.agent_id}-max-tokens`}
              disabled={!local.max_tokens_enabled}
              className={!local.max_tokens_enabled ? "opacity-50 cursor-not-allowed" : ""}
              placeholder="Maksymalna liczba tokenów odpowiedzi"
            />
            <p className="text-[11px] text-gray-500 leading-snug">
              {local.max_tokens_enabled
                ? "PAX wyśle dokładnie tę wartość do providera."
                : "Wyłączone — PAX nie wysyła parametru limitu do providera."}
            </p>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs text-gray-600">Timeout (sekundy, 0–600)</Label>
          <Input
            type="number"
            step="5"
            min="0"
            max="600"
            value={local.timeout_seconds ?? 0}
            onChange={(e) => set("timeout_seconds", parseInt(e.target.value, 10))}
            data-testid={`agent-${agent.agent_id}-timeout-seconds`}
          />
          <p className="text-[11px] text-gray-500 leading-snug">
            0 = brak limitu narzucanego przez PAX (timeout zostaje po stronie providera lub HTTP).
          </p>
        </div>

        <div className="flex flex-col gap-3 pt-2 border-t border-gray-100">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleTestConnection}
              disabled={testing}
              data-testid={`agent-${agent.agent_id}-test-connection-btn`}
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenPlayground(local)}
              data-testid={`agent-${agent.agent_id}-playground-btn`}
              className="border-blue-200 text-blue-700 hover:bg-blue-50"
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              Playground
            </Button>
            {agent.agent_id === "invoice_agent" && onOpenMemory && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenMemory}
                data-testid={`agent-${agent.agent_id}-memory-btn`}
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
              >
                <Brain className="w-4 h-4 mr-2" />
                Pamięć kontrahentów
              </Button>
            )}
          </div>

          {testResult && (
            <div
              className={`text-xs rounded px-3 py-2 border space-y-1 ${
                testResult.ok
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-red-50 border-red-200 text-red-800"
              }`}
              data-testid={`agent-${agent.agent_id}-test-result`}
            >
              <div className="flex flex-wrap items-center gap-2">
                {testResult.ok ? (
                  <span className="font-semibold inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> OK
                  </span>
                ) : (
                  <span className="font-semibold inline-flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Błąd
                  </span>
                )}
                {typeof testResult.latency_ms === "number" && (
                  <span>latency: <strong>{testResult.latency_ms} ms</strong></span>
                )}
                <span className="font-mono">{testResult.provider} / {testResult.model}</span>
              </div>
              {testResult.response_text && (
                <div className="font-mono text-xs break-words">
                  Odpowiedź: <span className="font-semibold">{testResult.response_text}</span>
                </div>
              )}
              {testResult.error && (
                <div className="whitespace-pre-wrap break-words">
                  {testResult.error}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={() => onSave(agent.agent_id, local)}
              disabled={saving}
              data-testid={`agent-${agent.agent_id}-save-btn`}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Zapisz
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AiAgentsSettingsPage = () => {
  const [agents, setAgents] = useState(null);
  const [providers, setProviders] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [confirmDisable, setConfirmDisable] = useState(null); // {agentId, agentName, patch} | null
  const [playgroundAgent, setPlaygroundAgent] = useState(null);
  const [memoryOpen, setMemoryOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ai/agents/settings`, { withCredentials: true });
      setAgents(res.data.agents || {});
      setProviders(res.data.providers || {});
    } catch (e) {
      logger.error("ai-agents settings fetch error", e);
      toast.error(e.response?.data?.detail || "Nie udało się wczytać ustawień");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /**
   * K-2 PATCH semantics + W-3 enable=false protection.
   *
   * - `payload` to AKTUALNY stan formularza (z UI).
   * - `baseline` to ostatnio załadowane ustawienia z backendu (`agents[agentId]`).
   * - Wysyłamy WYŁĄCZNIE pola, których wartość różni się od baseline (dirty).
   * - Pusty diff → zero requestów, toast informacyjny.
   * - `enabled: true → false` → modal potwierdzający przed wysłaniem.
   */
  const _buildPatch = (payload, baseline) => {
    const fields = ["enabled", "provider", "model", "system_prompt",
                    "user_prompt_template", "temperature",
                    "max_tokens_enabled", "max_tokens",
                    "timeout_seconds"];
    const patch = {};
    for (const k of fields) {
      const cur = payload?.[k];
      const base = baseline?.[k];
      // Normalizacja: traktuj undefined === null jako brak
      const a = (cur === undefined || cur === null) ? null : cur;
      const b = (base === undefined || base === null) ? null : base;
      if (k === "enabled" || k === "max_tokens_enabled") {
        if (Boolean(a) !== Boolean(b)) patch[k] = !!a;
      } else if (typeof a === "number" || typeof b === "number") {
        if (Number(a) !== Number(b)) patch[k] = a;
      } else {
        // string fields
        if (String(a ?? "") !== String(b ?? "")) patch[k] = a;
      }
    }
    return patch;
  };

  const _sendPatch = async (agentId, patch) => {
    const res = await axios.post(
      `${API}/ai/agents/settings/${agentId}`,
      patch,
      { withCredentials: true },
    );
    setAgents((prev) => ({ ...prev, [agentId]: res.data.agent }));
    setProviders(res.data.providers || providers);
    toast.success(`Zapisano ustawienia: ${res.data.agent.agent_name}`);
  };

  const handleSave = async (agentId, payload) => {
    const baseline = agents?.[agentId] || {};
    const patch = _buildPatch(payload, baseline);

    if (Object.keys(patch).length === 0) {
      toast.info("Brak zmian do zapisania.");
      return;
    }

    // W-3: wyłączanie agenta wymaga świadomego potwierdzenia
    if (patch.enabled === false) {
      setConfirmDisable({
        agentId,
        agentName: baseline?.agent_name || agentId,
        patch,
      });
      return;
    }

    setSavingId(agentId);
    try {
      await _sendPatch(agentId, patch);
    } catch (e) {
      logger.error("save agent error", e);
      const detail = e.response?.data?.detail;
      let msg = "Błąd zapisu ustawień";
      if (Array.isArray(detail)) msg = detail.map((d) => d.msg).join("; ");
      else if (typeof detail === "string") msg = detail;
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  const handleConfirmDisable = async () => {
    if (!confirmDisable) return;
    const { agentId, patch } = confirmDisable;
    setConfirmDisable(null);
    setSavingId(agentId);
    try {
      await _sendPatch(agentId, patch);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ")
                  : (typeof detail === "string" ? detail : "Błąd wyłączania agenta");
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-agents-settings-page">
      <div className="flex items-center gap-3">
        <Link to="/settings" data-testid="back-to-settings-link">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="w-4 h-4 mr-1" /> Ustawienia
          </Button>
        </Link>
        <div>
          <h1 className="page-title">Ustawienia agentów AI</h1>
          <p className="page-subtitle">
            Każdy agent ma własny provider, model, prompt i pamięć.
          </p>
        </div>
      </div>

      {/* Status kluczy ENV */}
      {providers && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Status kluczy API (ENV / Secret Store)</CardTitle>
            <CardDescription className="text-xs">
              Klucze pochodzą ze zmiennych środowiskowych aplikacji lub z zaszyfrowanego Secret Store w MongoDB.
              UI pokazuje tylko czy klucz istnieje — wartości nie są nigdy ujawniane.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <ProviderStatusBadge ok={!!providers.openai?.configured} label="OpenAI" />
              <ProviderStatusBadge ok={!!providers.anthropic?.configured} label="Anthropic" />
              <ProviderStatusBadge ok={!!providers.gemini?.configured} label="Gemini" />
              <ProviderStatusBadge ok={!!providers.minimax?.configured} label="MiniMax" />
              <ProviderStatusBadge ok={!!providers.openrouter?.configured} label="OpenRouter" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Klucze API — zarządzanie Secret Store */}
      {providers && (
        <ApiKeysCard providers={providers} onChanged={load} />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Wczytywanie…
        </div>
      ) : !agents ? (
        <div className="text-sm text-gray-500">Brak danych.</div>
      ) : (
        <div className="space-y-4">
          {AGENT_ORDER.map((aid) =>
            agents[aid] ? (
              <AgentCard
                key={aid}
                agent={agents[aid]}
                providers={providers}
                onSave={handleSave}
                saving={savingId === aid}
                onOpenPlayground={(localAgent) => setPlaygroundAgent(localAgent)}
                onOpenMemory={() => setMemoryOpen(true)}
              />
            ) : null
          )}
        </div>
      )}

      <AgentPlaygroundDialog
        open={!!playgroundAgent}
        onOpenChange={(o) => { if (!o) setPlaygroundAgent(null); }}
        agent={playgroundAgent}
      />
      <VendorMemoryDialog
        open={memoryOpen}
        onOpenChange={setMemoryOpen}
      />

      <AlertDialog open={!!confirmDisable} onOpenChange={(o) => !o && setConfirmDisable(null)}>
        <AlertDialogContent data-testid="agent-disable-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Wyłączyć agenta?</AlertDialogTitle>
            <AlertDialogDescription>
              Agent <strong>{confirmDisable?.agentName}</strong> zostanie wyłączony.
              Po wyłączeniu przestanie wykonywać analizy — żaden moduł aplikacji
              nie wywoła już tego agenta, dopóki nie włączysz go ponownie.
              <br /><br />
              Czy na pewno chcesz kontynuować?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="agent-disable-cancel">Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDisable}
              data-testid="agent-disable-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              Wyłącz agenta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AiAgentsSettingsPage;
